import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/Auth";
import "@/App.css";

import Welcome from "@/pages/Welcome";
import BuyerEntry from "@/pages/BuyerEntry";
import BuyerCity from "@/pages/BuyerCity";
import Marketplace from "@/pages/Marketplace";
import ProductDetail from "@/pages/ProductDetail";
import SellerAuth from "@/pages/SellerAuth";
import SellerOnboard from "@/pages/SellerOnboard";
import ChangePassword from "@/pages/ChangePassword";
import SellerLayout from "@/pages/seller/SellerLayout";
import SellerDashboard from "@/pages/seller/SellerDashboard";
import MyProducts from "@/pages/seller/MyProducts";
import AddProduct from "@/pages/seller/AddProduct";
import Subscription from "@/pages/seller/Subscription";
import SellerExtras from "@/pages/seller/SellerExtras";
import CompanyProfile from "@/pages/seller/CompanyProfile";
import WholesalersListing from "@/pages/seller/WholesalersListing";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import PendingProducts from "@/pages/admin/PendingProducts";
import AdminProducts from "@/pages/admin/AdminProducts";
import Sellers from "@/pages/admin/Sellers";
import RestrictedItems from "@/pages/admin/RestrictedItems";
import MasterData from "@/pages/admin/MasterData";
import PlansAdmin from "@/pages/admin/PlansAdmin";
import Holidays from "@/pages/admin/Holidays";
import InstagramSchedule from "@/pages/admin/InstagramSchedule";
import AdminExtras from "@/pages/admin/AdminExtras";
import IncompleteSignups from "@/pages/admin/IncompleteSignups";
import Subscriptions from "@/pages/admin/Subscriptions";
import AddProductAdmin from "@/pages/admin/AddProductAdmin";
import ClicksHistory from "@/pages/admin/ClicksHistory";
import Leads from "@/pages/admin/Leads";
import Referrals from "@/pages/admin/Referrals";
import Invoices from "@/pages/admin/Invoices";
import PaymentQR from "@/pages/admin/PaymentQR";
import SellerHistory from "@/pages/seller/SellerHistory";
import SellerCRM from "@/pages/seller/SellerCRM";
import ReferorAuth from "@/pages/ReferorAuth";
import ReferorLayout from "@/pages/referor/ReferorLayout";
import ReferorDashboard from "@/pages/referor/ReferorDashboard";
import ReferBusiness from "@/pages/referor/ReferBusiness";
import ReferorEarnings from "@/pages/referor/ReferorEarnings";
import PayoutSetup from "@/pages/referor/PayoutSetup";
import ReferorManagement from "@/pages/admin/ReferorManagement";
import ReferorPayments from "@/pages/admin/ReferorPayments";

function Guard({ role, children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="flex h-screen items-center justify-center text-slate-400">Loading…</div>;
  if (!user) return <Navigate to={role === "admin" ? "/admin/login" : role === "referor" ? "/referor" : "/seller"} replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors />
          <Routes>
            <Route path="/" element={<Welcome />} />
            <Route path="/buyer" element={<BuyerEntry />} />
            <Route path="/buyer/city" element={<BuyerCity />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/product/:id" element={<ProductDetail />} />

            <Route path="/seller" element={<SellerAuth />} />
            <Route path="/seller/change-password" element={<Guard role="seller"><ChangePassword /></Guard>} />
            <Route path="/seller/onboard" element={<Guard role="seller"><SellerOnboard /></Guard>} />
            <Route path="/seller/app" element={<Guard role="seller"><SellerLayout /></Guard>}>
              <Route index element={<SellerDashboard />} />
              <Route path="profile" element={<CompanyProfile />} />
              <Route path="products" element={<MyProducts />} />
              <Route path="products/new" element={<AddProduct />} />
              <Route path="products/:id/edit" element={<AddProduct />} />
              <Route path="subscription" element={<Subscription />} />
              <Route path="crm" element={<SellerCRM />} />
              <Route path="wholesalers" element={<WholesalersListing />} />
              <Route path="history" element={<SellerHistory />} />
              <Route path="more" element={<SellerExtras />} />
            </Route>

            <Route path="/referor" element={<ReferorAuth />} />
            <Route path="/referor/app" element={<Guard role="referor"><ReferorLayout /></Guard>}>
              <Route index element={<ReferorDashboard />} />
              <Route path="refer" element={<ReferBusiness />} />
              <Route path="earnings" element={<ReferorEarnings />} />
              <Route path="payout" element={<PayoutSetup />} />
            </Route>

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<Guard role="admin"><AdminLayout /></Guard>}>
              <Route index element={<AdminDashboard />} />
              <Route path="pending" element={<PendingProducts />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="sellers" element={<Sellers />} />
              <Route path="incomplete" element={<IncompleteSignups />} />
              <Route path="subscriptions" element={<Subscriptions />} />
              <Route path="add-product" element={<AddProductAdmin />} />
              <Route path="clicks" element={<ClicksHistory />} />
              <Route path="leads" element={<Leads />} />
              <Route path="referrals" element={<Referrals />} />
              <Route path="referors" element={<ReferorManagement />} />
              <Route path="referor-payments" element={<ReferorPayments />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="payment-qr" element={<PaymentQR />} />
              <Route path="restricted" element={<RestrictedItems />} />
              <Route path="master" element={<MasterData />} />
              <Route path="plans" element={<PlansAdmin />} />
              <Route path="holidays" element={<Holidays />} />
              <Route path="instagram" element={<InstagramSchedule />} />
              <Route path="more" element={<AdminExtras />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
